import { BadgeStyle } from '../../../shared/enums/status';
import type { FilterOption } from '../../../types/filterOptionTypes';
import type {
  TicketCategory,
  TicketOrigin,
  TicketPriority,
  TicketStatus,
} from '../../../types/support';

/**
 * Status/category/priority metadata shared by the queue table and the
 * ticket detail header, kept in one place so the two never label the same
 * value differently.
 */

const ALL_DOT = 'status-option-dot-all';

export const TICKET_STATUS_BADGE_STYLE: Record<TicketStatus, BadgeStyle> = {
  bot_handling: BadgeStyle.PENDING,
  // The reason an agent opens this page — it should read as urgent.
  needs_human: BadgeStyle.FAILURE,
  in_progress: BadgeStyle.IN_USE,
  waiting_user: BadgeStyle.PENDING,
  resolved: BadgeStyle.ACTIVE,
  closed: BadgeStyle.INACTIVE,
};

export const TICKET_PRIORITY_BADGE_STYLE: Record<TicketPriority, BadgeStyle> = {
  low: BadgeStyle.INACTIVE,
  normal: BadgeStyle.PENDING,
  high: BadgeStyle.FAILURE,
};

export const TICKET_STATUSES: readonly TicketStatus[] = [
  'bot_handling',
  'needs_human',
  'in_progress',
  'waiting_user',
  'resolved',
  'closed',
];

export const TICKET_CATEGORIES: readonly TicketCategory[] = [
  'charging',
  'payment',
  'account',
  'station_fault',
  'other',
];

export const TICKET_PRIORITIES: readonly TicketPriority[] = ['low', 'normal', 'high'];

export const ticketStatusLabelKey = (status: TicketStatus): string =>
  `dashboard.support.status.${status}`;

export const ticketCategoryLabelKey = (category: TicketCategory): string =>
  `dashboard.support.category.${category}`;

export const ticketPriorityLabelKey = (priority: TicketPriority): string =>
  `dashboard.support.priority.${priority}`;

export const ticketOriginLabelKey = (origin: TicketOrigin): string =>
  `dashboard.support.origin.${origin}`;

export const TICKET_ORIGIN_OPTIONS: readonly FilterOption<TicketOrigin>[] = [
  { value: '', label: 'dashboard.support.allOrigins', dotClass: ALL_DOT },
  {
    value: 'bot',
    label: ticketOriginLabelKey('bot'),
    dotClass: `status-option-dot-${BadgeStyle.PENDING}`,
  },
  {
    value: 'agent',
    label: ticketOriginLabelKey('agent'),
    dotClass: `status-option-dot-${BadgeStyle.IN_USE}`,
  },
];

export const TICKET_STATUS_OPTIONS: readonly FilterOption<TicketStatus>[] = [
  { value: '', label: 'dashboard.support.allStatuses', dotClass: ALL_DOT },
  ...TICKET_STATUSES.map((status) => ({
    value: status,
    label: ticketStatusLabelKey(status),
    dotClass: `status-option-dot-${TICKET_STATUS_BADGE_STYLE[status]}`,
  })),
];

export const TICKET_CATEGORY_OPTIONS: readonly FilterOption<TicketCategory>[] = [
  { value: '', label: 'dashboard.support.allCategories', dotClass: ALL_DOT },
  ...TICKET_CATEGORIES.map((category) => ({
    value: category,
    label: ticketCategoryLabelKey(category),
    dotClass: 'status-option-dot-active',
  })),
];

type ChipColor = 'default' | 'warning' | 'error' | 'info' | 'success';

/** MUI Chip color for the detail header — a different rendering surface
 * from the queue table's `StatusBadgeCell`, but driven by the same status. */
export const TICKET_STATUS_CHIP_COLOR: Record<TicketStatus, ChipColor> = {
  bot_handling: 'info',
  needs_human: 'error',
  in_progress: 'info',
  waiting_user: 'warning',
  resolved: 'success',
  closed: 'default',
};

export const TICKET_PRIORITY_CHIP_COLOR: Record<TicketPriority, ChipColor> = {
  low: 'default',
  normal: 'warning',
  high: 'error',
};
